// @flow
import React, { Component } from 'react'
import _ from 'lodash'
import { Table, HeaderCell, HeaderRow, Cell, BodyRow, Link } from './MainTable'
import AmountInput from './AgencyPage/AmountInput'
import Modal from './FieldSet/Modal'
import AccountsImport from './AgencyPage/AccountsImport'
import type { OptionType } from './types'
import postJson from '../services/postJson'
import styled from 'styled-components'
import NamePopup from './AgencyPage/NamePopup'
import CreateFormContainer from './AgencyPage/CreateFormContainer'
import PrimaryButton from './controls/PrimaryButton'

export type AdAccount = {|
  id: string,
  name: string,
  balance: number,
  blockedBalance: number,
  accountBilling: boolean,
  switchAdAccountUrl: string,
  editAdAccountUrl: string,
  replenishUrl: string,
  withdrawUrl: string
|}

export type LoadedAccount = {|
  id: number,
  name: string,
  account_id: number,
  in_agency: boolean
|}

export type ImportError = {|
  accountName: string,
  errors: Array<string>
|}

type Props = {|
  initialAdAccounts: Array<AdAccount>,
  adAccountsList: Array<AdAccount>,
  agencyVkTokenUid: string,
  initialVkLoadedAccounts: Array<LoadedAccount>,
  vkLoadedAccountsList: Array<LoadedAccount>,
  accountBillingModelOptions: Array<OptionType>,
  isModalOpen: boolean,
  isLoading: boolean,
  reloadErrors: Array<string>,
  importErrors: Array<ImportError>,
  onInit: Function,
  onChange: Function,
  onImportAccounts: Function
|}

const t = {
  ad_accounts: 'Рекламные кабинеты',
  balance: 'Доступно',
  blocked_balance: 'Заблокировано',
  client: 'Клиент',
  import_vk_accounts: 'Привязать свои аккаунты из ВКонтакте',
  get_token: 'Получить токен для импортирования кабинетов',
  replenish: 'Пополнить кабинет',
  withdraw: 'Вернуть в агенство',
  replenishment_from_agency: 'Пополняется агенством',
  replenishment_on_its_own: 'Пополняется клиентом самостоятельно'
}

const RELOAD_ACCOUNTS_URL = '/agencies/agency/reload_accounts'
const GET_TOKEN_URL = '/agencies/agency/get_token'

const AccountBillingComment = styled.div`
  color: gray;
  font-size: 12px;
`

export default class AgencyPage extends Component<Props> {
  debouncedReload: any

  constructor(props: Props) {
    super(props)

    props.onInit({
      adAccountsList: props.initialAdAccounts,
      vkLoadedAccountsList: props.initialVkLoadedAccounts
    })

    this.debouncedReload = _.debounce(this.reloadAccounts, 1000)
  }

  render() {
    const {
      isLoading,
      isModalOpen,
      adAccountsList,
      vkLoadedAccountsList,
      agencyVkTokenUid,
      reloadErrors,
      importErrors,
      onChange,
      onImportAccounts
    } = this.props

    return (
      <div>
        <Modal
          maxWidth={800}
          header={t.import_vk_accounts}
          isOpen={isModalOpen}
          hideBtn
          onClose={() => onChange({ isModalOpen: false })}
        >
          <AccountsImport
            isLoading={isLoading}
            accountOptions={this.accountOptions()}
            vkLoadedAccounts={vkLoadedAccountsList}
            reloadErrors={reloadErrors}
            importErrors={importErrors}
            onImportAccounts={(accounts) => onImportAccounts(accounts)}
            onReloadAccounts={() => this.debouncedReload()}
          />
        </Modal>
        <div className="top-info">
          <h1>{t.ad_accounts}</h1>
          <CreateFormContainer
            agencyBillingModelOptions={this.props.accountBillingModelOptions}
          />{' '}
          {_.isEmpty(agencyVkTokenUid) ? (
            <PrimaryButton small href={GET_TOKEN_URL} target="_blank">
              {t.get_token}
            </PrimaryButton>
          ) : (
            <PrimaryButton small onClick={() => onChange({ isModalOpen: true })}>
              {t.import_vk_accounts}
            </PrimaryButton>
          )}
        </div>
        <Table>
          <thead>
            <HeaderRow>
              <HeaderCell alignLeft>#</HeaderCell>
              <HeaderCell alignLeft>{t.client}</HeaderCell>
              <HeaderCell>{t.balance}</HeaderCell>
              <HeaderCell>{t.blocked_balance}</HeaderCell>
              <HeaderCell>{t.replenish}</HeaderCell>
              <HeaderCell>{t.withdraw}</HeaderCell>
            </HeaderRow>
          </thead>
          <tbody>
            {_.map(adAccountsList, (adAccount) => {
              const {
                id,
                name,
                balance,
                blockedBalance,
                accountBilling,
                switchAdAccountUrl,
                replenishUrl,
                withdrawUrl
              } = adAccount
              return (
                <BodyRow key={id}>
                  <Cell>{id}</Cell>
                  <Cell>
                    <div>
                      <Link data-method="post" href={switchAdAccountUrl}>
                        {name}
                      </Link>{' '}
                      <NamePopup
                        value={name}
                        id={id}
                        onChange={(name) => this.adAccountNameChanged(id, name)}
                      />
                    </div>
                    <AccountBillingComment>
                      {accountBilling ? t.replenishment_on_its_own : t.replenishment_from_agency}
                    </AccountBillingComment>
                  </Cell>
                  <Cell alignRight>{balance}</Cell>
                  <Cell alignRight>{blockedBalance}</Cell>
                  <Cell alignRight>
                    <AmountInput
                      name="replenish"
                      url={replenishUrl}
                      disabled={accountBilling}
                      onAmountChange={(balance, blockedBalance) =>
                        this.processAmountChange(adAccount, balance, blockedBalance)
                      }
                    />
                  </Cell>
                  <Cell alignRight>
                    <AmountInput
                      name="withdraw"
                      url={withdrawUrl}
                      disabled={accountBilling}
                      onAmountChange={(balance, blockedBalance) =>
                        this.processAmountChange(adAccount, balance, blockedBalance)
                      }
                    />
                  </Cell>
                </BodyRow>
              )
            })}
          </tbody>
        </Table>
      </div>
    )
  }

  adAccountNameChanged(id: string, name: string) {
    const { adAccountsList, onChange } = this.props
    const newAdAccountsList = _.map(adAccountsList, (adAccount) => {
      return adAccount.id === id
        ? {
            ...adAccount,
            name: name
          }
        : adAccount
    })
    onChange({ adAccountsList: newAdAccountsList })
  }

  processAmountChange(account: AdAccount, balance: number, blockedBalance: number) {
    const { adAccountsList, onChange } = this.props
    const newAdAccountsList = _.map(adAccountsList, (adAccount) => {
      return adAccount.id === account.id
        ? {
            ...account,
            balance: balance,
            blockedBalance: blockedBalance
          }
        : adAccount
    })
    onChange({ adAccountsList: newAdAccountsList })
  }

  accountOptions(): Array<OptionType> {
    return _.map(this.props.adAccountsList, (adAccount) => {
      return { id: adAccount.id, name: adAccount.name }
    })
  }

  reloadAccounts() {
    this.props.onChange({ isLoading: true, reloadErrors: [] })
    postJson(RELOAD_ACCOUNTS_URL)
      .done((response) => {
        if (response.ready) {
          this.props.onChange({ isLoading: false, vkLoadedAccountsList: response.accounts })
        } else if (!_.isEmpty(response.errors)) {
          this.props.onChange({ isLoading: false, reloadErrors: response.errors })
        } else {
          this.debouncedReload()
        }
      })
      .fail(() => window.alert('Internal error'))
  }
}
